import { useMemo } from "react";
import { Cell, useSortBy, useTable } from "react-table";
import {
  TableContainer,
  Th,
  Thead,
  Tr,
  Table,
  Tbody,
  Td,
  useColorMode,
  Flex,
  Box,
} from "@chakra-ui/react";
import { HiArrowsUpDown, HiArrowUp, HiArrowDown } from "react-icons/hi2";

// Types
import { DefiData } from "@customtypes/global";

// Helpers
import { getColumns } from "./helpers";
import { currencyToNumber } from "utils/common";

const DefiTable = ({
  group,
  isSDPool,
  data,
  defiData,
  hasAPY,
}: {
  group: string;
  isSDPool: boolean;
  data: any[];
  defiData: DefiData;
  hasAPY: boolean;
}) => {
  const { colorMode } = useColorMode();

  const dataDefi = useMemo(
    () =>
      data.map((d: any) => ({
        ...d,
        tvlInNumbers: defiData[d.apiKey]?.tvl
          ? currencyToNumber(defiData[d.apiKey]?.tvl)
          : 0,
        tvl: defiData[d.apiKey]?.tvl,
        apy: Number(defiData[d.apiKey]?.apy),
        isSDPool,
      })),
    [data, defiData, isSDPool]
  );

  const columns = useMemo(
    () => getColumns(hasAPY, defiData, colorMode, !(data.length > 1)),
    [colorMode, hasAPY, defiData, data]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        data: dataDefi,
        columns,
        initialState: { sortBy: [{ id: "tvlInNumbers", desc: true }] },
      },
      useSortBy
    );

  return (
    <TableContainer
      gap="2.5rem"
      mt="4"
      flexDir="column"
      bg="backgroundSecondary"
      p={{ base: "1rem 1.5rem", lg: "1.25rem 2.5rem 1rem" }}
      borderRadius="1.5rem"
    >
      <Table variant="simple" {...getTableProps()}>
        <Thead>
          {headerGroups.map((headerGroup) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={`${group}-header`}>
              {headerGroup.headers.map((column) => (
                <Th
                  {...column.getHeaderProps(
                    (column as any).getSortByToggleProps()
                  )}
                  key={column.id}
                  fontSize={14}
                  fontWeight="700"
                  color="textSecondary"
                  textTransform="none"
                  width={column.width}
                >
                  <Flex align="center" gap="0.5rem">
                    {column.render("Header")}
                    {(column as any).canSort && (
                      <Box>
                        {(column as any).isSorted ? (
                          (column as any).isSortedDesc ? (
                            <HiArrowDown />
                          ) : (
                            <HiArrowUp />
                          )
                        ) : (
                          <HiArrowsUpDown />
                        )}
                      </Box>
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map((row: any) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} key={row.original.key}>
                {row.cells.map((cell: Cell) => {
                  return (
                    <Td
                      {...cell.getCellProps()}
                      key={`${cell.column.id}-${cell.row.id}`}
                    >
                      {cell.render("Cell")}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default DefiTable;
